import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { File } from 'types/policyService';
import { getPolicyByEnrollmentPeriodId, getPreSignUrl } from 'api/operations/policyService';
import { createContext, useContext } from 'react';
import ModalDelegate from 'store/general/modalDelegate';
import EnrollmentPeriodModule from 'store/modules/enrollment/enrollmentPeriodModule';
import { requestApplyPostSubmitUserAction } from 'api/operations/enrollmentService';
import { USER_ACTIONS } from 'types/enrollmentService';
import { retryRequestFor } from 'global/utils/retryRequestFor';
import { getRootStoreInstance, RootStore } from 'store/rootStore';
import { Feature } from 'types/domainServiceV2';
import { downloadByUrl } from '@insurancemenu/imenu-components-lib/dist/helpers/downloadByUrl';

interface DomainProps {
  enrollmentPeriodId: string;
}

export class EnrollmentPacketPageStore {
  private rootStore: RootStore = getRootStoreInstance();
  private readonly _enrollmentPeriodId: string;
  private readonly _enrollmentPeriodModule = new EnrollmentPeriodModule();
  private _policyPdfs: File[] = [];
  private _finalZip?: File = undefined;
  private _isLoading = false;
  public loadErrorModal = new ModalDelegate();
  public submittedDocsWarningModal = new ModalDelegate();

  get isLoading(): boolean {
    return this._isLoading;
  }

  get policyPdfs(): File[] {
    return this._policyPdfs;
  }

  get finalZip(): File | undefined {
    return this._finalZip;
  }

  get enrollmentPeriodId(): string {
    return this._enrollmentPeriodId;
  }

  public get creationDate(): string {
    return (this.policyPdfs.length > 0 && this.policyPdfs[0].creationDate) || '';
  }

  public async uncompleteDocumentsStep(): Promise<void> {
    await requestApplyPostSubmitUserAction(this._enrollmentPeriodId, USER_ACTIONS.UNCOMPLETE_POST_SUBMIT_DOCUMENTS);
  }

  public setLoading(status: boolean): void {
    this._isLoading = status;
  }

  public async uploadDomainData(): Promise<void> {
    await this._enrollmentPeriodModule.uploadEnrollmentPeriod(this._enrollmentPeriodId);
  }

  public async buildPdfsList(): Promise<void> {
    this.resetFiles();
    this.setLoading(true);

    try {
      const policy = await retryRequestFor(() => getPolicyByEnrollmentPeriodId(this._enrollmentPeriodId), 10000, 2000);
      runInAction(() => {
        this._policyPdfs = policy.pdfs;
        this._finalZip = policy.finalZip;
      });
    } catch {
      runInAction(() => {
        this.loadErrorModal.open();
      });
    } finally {
      this.setLoading(false);
    }
  }

  public async downloadFinalZip(): Promise<void> {
    if (this._finalZip && this._finalZip.id) {
      const zipLink = await getPreSignUrl(this._finalZip.id);
      downloadByUrl(zipLink);
    }
  }

  public resetFiles(): void {
    this.loadErrorModal.close();
    this._policyPdfs = [];
    this._finalZip = undefined;
  }

  public get isESignatureAvailable(): boolean {
    return (
      this.rootStore.domainStoreV2.domainHeader.properties.availableFeatures?.includes(Feature.E_SIGNATURE) || false
    );
  }

  constructor({ enrollmentPeriodId }: DomainProps) {
    this._enrollmentPeriodId = enrollmentPeriodId;
    makeObservable<EnrollmentPacketPageStore, '_policyPdfs' | '_finalZip' | '_isLoading'>(this, {
      _isLoading: observable,
      _policyPdfs: observable,
      _finalZip: observable,
      loadErrorModal: observable,
      submittedDocsWarningModal: observable,
      enrollmentPeriodId: computed,
      isLoading: computed,
      policyPdfs: computed,
      creationDate: computed,
      isESignatureAvailable: computed,
      uncompleteDocumentsStep: action.bound,
      buildPdfsList: action.bound,
      setLoading: action.bound,
      uploadDomainData: action.bound,
      downloadFinalZip: action.bound,
    });
  }
}

const EnrollmentPacketPageStoreContext = createContext(
  new EnrollmentPacketPageStore({ enrollmentPeriodId: 'stub-period' }),
);

export const useEnrollmentPacketPageStore = (): EnrollmentPacketPageStore => {
  return useContext(EnrollmentPacketPageStoreContext);
};

export { EnrollmentPacketPageStoreContext };
